import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { useDevice } from '@/app/DeviceProvider';
import { hostVetrina } from '@/app/marca';
import { useSessioneTecnica } from '@/app/sessioneTecnica';
import { Avviso, Bottone, Campo, Card, cx, stili } from '@/ui';
import s from '../features.module.css';

/** Le credenziali di dimostrazione, gia' scritte in chiaro nel suggerimento
 *  sotto il modulo (`tecnico.accesso.demoHint`) e nel simulatore che le
 *  verifica. Qui stanno per essere PRECOMPILATE, non per essere rivelate. */
const UTENTE_DEMO = 'tecnico';
const PASSWORD_DEMO = 'emacomfort';

export function AccessoPage() {
  const { t } = useTranslation();
  const { client } = useDevice();
  const navigate = useNavigate();
  const location = useLocation();
  const { sessione, accedi, motivoUscita } = useSessioneTecnica();
  // Su un host vetrina i campi arrivano gia' compilati.
  //
  // Non e' un accesso aggirato: il modulo si invia davvero, la centralina
  // simulata verifica davvero e la sessione e' quella vera, con la sua
  // scadenza per inattivita' (RNF-25). E' che su quell'host le credenziali
  // sono gia' PUBBLICATE due righe piu' sotto — farle ricopiare a mano non
  // protegge niente, allontana soltanto un valutatore dall'unica area che
  // gli interessa davvero.
  //
  // 🚨 Solo in vetrina. Su un host installatore i campi restano vuoti: la'
  // le credenziali sono di qualcuno, e la produzione non ha credenziali
  // predefinite di alcun tipo (RNF-21).
  const vetrina = typeof window !== 'undefined' && hostVetrina(window.location.hostname);
  const [utente, setUtente] = useState(vetrina ? UTENTE_DEMO : '');
  const [password, setPassword] = useState(vetrina ? PASSWORD_DEMO : '');
  const [errore, setErrore] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const destinazione = (location.state as { da?: string } | null)?.da ?? '/tecnico/parco';

  if (sessione) return <Navigate to={destinazione} replace />;

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    setInCorso(true);
    setErrore(false);
    try {
      const sess = await client.tecnico.accedi({ utente: utente.trim(), password });
      accedi(sess);
      navigate(destinazione, { replace: true });
    } catch {
      setErrore(true);
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className={s.accesso}>
      <Card className={s.accessoCarta} titolo={t('tecnico.accesso.titolo')} icona={<LockKeyhole size={20} aria-hidden />}>
        <p className={s.letturaNota}>{t('tecnico.accesso.sottotitolo')}</p>
        {motivoUscita === 'scaduta' && <Avviso tono="warn">{t('tecnico.accesso.sessioneScaduta')}</Avviso>}
        <form onSubmit={(e) => void invia(e)} className={cx(stili.griglia)}>
          <Campo etichetta={t('tecnico.accesso.utente')}>
            <input className={stili.input} autoComplete="username" value={utente} onChange={(e) => setUtente(e.target.value)} required />
          </Campo>
          <Campo etichetta={t('tecnico.accesso.password')}>
            <input className={stili.input} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Campo>
          {errore && <Avviso tono="err">{t('tecnico.accesso.errore')}</Avviso>}
          <Bottone type="submit" variante="primario" pieno disabled={inCorso}>
            {t('tecnico.accesso.entra')}
          </Bottone>
        </form>
        <Avviso tono="info">{t('tecnico.accesso.demoHint')}</Avviso>
        <Link to="/impostazioni" className={cx(stili.bottone, stili.fantasma)}>
          {t('tecnico.accesso.tornaAlPannello')}
        </Link>
      </Card>
    </div>
  );
}
